import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { recipesService } from '../services/recipesService';
import { Recipe, DEFAULT_WEIGHT_UNIT, DEFAULT_VOLUME_UNIT } from 'public-contracts';
import { useRBAC } from '../hooks/useRBAC';
import { useToast } from '../hooks/useToast';
import { useListFetch } from '../hooks/useListFetch';
import { ListFetchStates } from '../components/common/ListFetchStates';
import { InventoryService, InventoryCreateEntry } from '../services/inventoryService';
import { ingredientsService } from '../services/ingredientsService';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';

// Lucide React icons
import {
  Plus,
  Edit,
  Trash2,
  Search,
  ChefHat,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  DollarSign,
  Info,
  ExternalLink,
  Wand2,
  Loader2,
} from 'lucide-react';

interface RecipeListProps {
  onCreateClick: () => void;
  onEditClick: (recipe: Recipe) => void;
}

export const ModernRecipeList: React.FC<RecipeListProps> = ({
  onCreateClick,
  onEditClick,
}) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useRBAC();
  const { success, error: showError } = useToast();
  const { data, isLoading, isError, fetchData } = useListFetch<{
    recipes: Recipe[];
    total: number;
  }>();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });

  const canDelete = hasPermission('recipes', 'delete');
  const [validationStatus, setValidationStatus] = useState<
    Map<string, boolean>
  >(new Map());
  const [validationDetails, setValidationDetails] = useState<
    Map<
      string,
      {
        valid: boolean;
        errors: string[];
        missingMappings: Array<{
          ingredientId: string;
          ingredientName?: string;
        }>;
      }
    >
  >(new Map());
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [selectedRecipeForValidation, setSelectedRecipeForValidation] =
    useState<Recipe | null>(null);
  const [costs, setCosts] = useState<Map<string, number>>(new Map());
  const [autoFixing, setAutoFixing] = useState(false);

  // Debounce search term and reset to page 1 when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm && pagination.page !== 1) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [searchTerm]);

  // Load validation status and costs for recipes (runs after main fetch)
  const loadRecipeDetails = useCallback(async (recipesArray: Recipe[]) => {
    for (const recipe of recipesArray) {
      try {
        const validation = await recipesService.validateRecipe(recipe.id);
        setValidationStatus((prev) =>
          new Map(prev).set(recipe.id, validation.valid)
        );
        setValidationDetails((prev) =>
          new Map(prev).set(recipe.id, validation)
        );

        const cost = await recipesService.getRecipeCost(recipe.id);
        setCosts((prev) => new Map(prev).set(recipe.id, cost.totalCost));
      } catch (err) {
        console.error(
          `Failed to load validation/cost for recipe ${recipe.id}:`,
          err
        );
      }
    }
  }, []);

  // Auto-fix missing ingredient-inventory mappings
  const handleAutoFixMappings = useCallback(async () => {
    if (!selectedRecipeForValidation) return;

    const validation = validationDetails.get(selectedRecipeForValidation.id);
    if (!validation || validation.missingMappings.length === 0) return;

    setAutoFixing(true);
    try {
      // Get warehouses and categories
      const [warehouses, categories] = await Promise.all([
        InventoryService.getWarehouses(),
        InventoryService.getInventoryCategories(),
      ]);

      if (warehouses.length === 0) {
        showError(
          translate(
            'pos.recipes.validation.autoFix.noWarehouse',
            'Cannot auto-fix: No warehouses found. Please create a warehouse first.'
          )
        );
        setAutoFixing(false);
        return;
      }

      if (categories.length === 0) {
        showError(
          translate(
            'pos.recipes.validation.autoFix.noCategory',
            'Cannot auto-fix: No inventory categories found. Please create an inventory category first.'
          )
        );
        setAutoFixing(false);
        return;
      }

      // Use first warehouse and first category as defaults
      const defaultWarehouse = warehouses[0];
      const defaultCategory = categories[0];

      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Process each missing mapping
      for (const mapping of validation.missingMappings) {
        try {
          // Get ingredient details if name is not provided
          let ingredientName = mapping.ingredientName;
          if (!ingredientName) {
            const ingredient = await ingredientsService.getIngredient(
              mapping.ingredientId
            );
            ingredientName = ingredient.name;
          }

          // Generate code for the inventory item
          const code = await InventoryService.generateNextCode(
            defaultCategory.id
          );

          // Create inventory item
          const inventoryData: InventoryCreateEntry = {
            code,
            name: ingredientName,
            description: `Auto-created inventory item for ingredient: ${ingredientName}`,
            quantity: 0,
            totalPrice: 0,
            currency: 'VND',
            warehouseId: defaultWarehouse.id,
            inventoryCategoryId: defaultCategory.id,
            ingredientId: mapping.ingredientId,
            width: 0,
            height: 0,
            weight: 0,
            weightUnit: DEFAULT_WEIGHT_UNIT,
            volume: 0,
            volumeUnit: DEFAULT_VOLUME_UNIT,
            isPackage: false,
          };

          await InventoryService.createInventory(inventoryData);
          successCount++;
        } catch (err) {
          errorCount++;
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'Unknown error occurred';
          errors.push(
            `${mapping.ingredientName || mapping.ingredientId}: ${errorMessage}`
          );
        }
      }

      // Show results
      if (successCount > 0) {
        success(
          translate(
            'pos.recipes.validation.autoFix.success',
            {
              count: successCount,
            },
            `Successfully created ${successCount} inventory item(s). Please review and configure package details manually.`
          )
        );
      }

      if (errorCount > 0) {
        showError(
          translate(
            'pos.recipes.validation.autoFix.partialError',
            {
              success: successCount,
              error: errorCount,
              details: errors.join('; '),
            },
            `Created ${successCount} item(s), but ${errorCount} failed: ${errors.join('; ')}`
          )
        );
      }

      // Refresh validation for this recipe
      if (successCount > 0) {
        const updatedValidation = await recipesService.validateRecipe(
          selectedRecipeForValidation.id
        );
        setValidationStatus((prev) =>
          new Map(prev).set(selectedRecipeForValidation.id, updatedValidation.valid)
        );
        setValidationDetails((prev) =>
          new Map(prev).set(selectedRecipeForValidation.id, updatedValidation)
        );
      }
    } catch (err) {
      console.error('Auto-fix error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to auto-fix mappings';
      showError(
        translate(
          'pos.recipes.validation.autoFix.error',
          { message: errorMessage },
          `Failed to auto-fix mappings: ${errorMessage}`
        )
      );
    } finally {
      setAutoFixing(false);
    }
  }, [selectedRecipeForValidation, validationDetails, translate, success, showError]);

  // Fetch data - follows OrdersReport pattern exactly
  const handleFetchRecipes = async () => {
    try {
      const response = await fetchData(async () => {
        const result = await recipesService.getRecipes({
          page: pagination.page,
          perPage: pagination.perPage,
          q: debouncedSearchTerm || undefined,
        });

        const recipesArray = Array.isArray(result) ? result : [];
        const total = (result as any).__total || recipesArray.length;

        return {
          recipes: recipesArray,
          total,
        };
      });

      if (response) {
        setRecipes(response.recipes);
        setPagination((prev) => ({ ...prev, total: response.total }));
        // Load validation and costs in background
        loadRecipeDetails(response.recipes);
      }
    } catch (error) {
      // Error is already handled by useListFetch
      setRecipes([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  // Auto-fetch data when component mounts or dependencies change
  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timer = setTimeout(
      () => {
        handleFetchRecipes();
      },
      debouncedSearchTerm ? 300 : 0
    );

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.perPage, debouncedSearchTerm]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleDelete = async () => {
    if (!recipeToDelete) return;

    if (!canDelete) {
      showError(translate('pos.common.accessDenied', 'Access Denied'));
      setDeleteDialogOpen(false);
      return;
    }

    try {
      await recipesService.deleteRecipe(recipeToDelete.id);
      setRecipes((prev) => prev.filter((r) => r.id !== recipeToDelete.id));
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
      success(
        translate('pos.recipes.delete.success', 'Recipe deleted successfully')
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete recipe';
      showError(errorMessage);
      setDeleteDialogOpen(false);
    }
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    // Immediately update debounced search term
    setDebouncedSearchTerm(searchTerm);
  };

  const handleShowValidationDetails = (recipe: Recipe) => {
    setSelectedRecipeForValidation(recipe);
    setValidationDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ChefHat className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {translate('pos.recipes.name', 'Recipe Management')}
          </h1>
        </div>
        <Button onClick={onCreateClick} className="flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>{translate('pos.recipes.add', 'Add Recipe')}</span>
        </Button>
      </div>

      {/* Loading and Error States - matches OrdersReport pattern */}
      <ListFetchStates
        isLoading={isLoading}
        isError={isError}
        onRetry={handleFetchRecipes}
        loadingMessage={translate('common.loading', 'Loading recipes...')}
        errorMessage={translate(
          'pos.recipes.load_error',
          'Failed to load recipes'
        )}
      />

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>{translate('common.search', 'Search')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={translate(
                  'pos.recipes.search',
                  'Search recipes...'
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit">
              {translate('common.search', 'Search')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Recipe Table - only show when not loading and no error */}
      {!isLoading && !isError && (
        <Card>
          <CardHeader>
            <CardTitle>{translate('pos.recipes.list', 'Recipes')}</CardTitle>
          </CardHeader>
          <CardContent>
            {recipes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {translate('pos.recipes.empty', 'No recipes found')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {translate('pos.recipes.name', 'Name')}
                    </TableHead>
                    <TableHead>
                      {translate('pos.recipes.ingredients', 'Ingredients')}
                    </TableHead>
                    <TableHead>
                      {translate('pos.recipes.status', 'Status')}
                    </TableHead>
                    <TableHead>
                      {translate('pos.recipes.cost', 'Cost')}
                    </TableHead>
                    <TableHead>
                      {translate('common.actions', 'Actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipes.map((recipe) => {
                    const isValid = validationStatus.get(recipe.id) ?? false;
                    const cost = costs.get(recipe.id) ?? 0;

                    return (
                      <TableRow key={recipe.id}>
                        <TableCell className="font-medium">
                          {recipe.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {recipe.ingredients?.length || 0}{' '}
                            {translate(
                              'pos.recipes.ingredients',
                              'ingredients'
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {isValid ? (
                              <Badge className="bg-green-500">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                {translate('pos.recipes.valid', 'Valid')}
                              </Badge>
                            ) : (
                              <>
                                <Badge variant="destructive">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  {translate('pos.recipes.invalid', 'Invalid')}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() =>
                                    handleShowValidationDetails(recipe)
                                  }
                                  title={translate(
                                    'pos.recipes.validation.details',
                                    'View validation details'
                                  )}
                                >
                                  <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-1">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <span>{cost.toLocaleString()} VND</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEditClick(recipe)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!canDelete) {
                                  showError(
                                    translate(
                                      'pos.common.accessDenied',
                                      'Access Denied'
                                    )
                                  );
                                  return;
                                }
                                setRecipeToDelete(recipe);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={!canDelete}
                              title={
                                !canDelete
                                  ? translate(
                                      'pos.common.accessDenied',
                                      'Access Denied'
                                    )
                                  : translate(
                                      'pos.recipes.delete',
                                      'Delete recipe'
                                    )
                              }
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.recipes.delete.title', 'Delete Recipe')}
            </DialogTitle>
            <DialogDescription>
              {translate('pos.recipes.delete.confirm', {
                name: recipeToDelete?.name || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {translate('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Details Dialog */}
      <Dialog
        open={validationDialogOpen}
        onOpenChange={setValidationDialogOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <span>
                {translate(
                  'pos.recipes.validation.title',
                  'Recipe Validation Details'
                )}
              </span>
            </DialogTitle>
            <DialogDescription>
              {selectedRecipeForValidation && (
                <span>
                  {translate('pos.recipes.validation.description', {
                    name: selectedRecipeForValidation.name,
                  })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedRecipeForValidation && (
              <>
                {(() => {
                  const validation = validationDetails.get(
                    selectedRecipeForValidation.id
                  );
                  if (!validation || validation.valid) {
                    return (
                      <div className="text-sm text-muted-foreground">
                        {translate(
                          'pos.recipes.validation.noErrors',
                          'No validation errors found.'
                        )}
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {(() => {
                        // Filter out mapping errors since they're shown in missingMappings section
                        const nonMappingErrors = validation.errors.filter(
                          (error) => {
                            // Skip errors about missing inventory mappings/linked
                            // Matches: "Ingredient X has no inventory mapping"
                            // Matches: "Ingredient X has no inventory linked"
                            return !error.match(
                              /Ingredient .+ has no inventory (mapping|linked)/i
                            );
                          }
                        );

                        return nonMappingErrors.length > 0 ? (
                          <div>
                            <h4 className="font-semibold mb-2 text-destructive">
                              {translate(
                                'pos.recipes.validation.errors',
                                'Validation Errors:'
                              )}
                            </h4>
                            <ul className="list-disc list-inside space-y-1">
                              {nonMappingErrors.map((error, index) => (
                                <li
                                  key={index}
                                  className="text-sm text-muted-foreground"
                                >
                                  {error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null;
                      })()}
                      {validation.missingMappings.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2 text-destructive">
                            {translate(
                              'pos.recipes.validation.missingMappings',
                              'Missing Inventory Mappings:'
                            )}
                          </h4>
                          <ul className="list-disc list-inside space-y-1">
                            {validation.missingMappings.map(
                              (mapping, index) => (
                                <li
                                  key={index}
                                  className="text-sm text-muted-foreground"
                                >
                                  {translate(
                                    'pos.recipes.validation.missingMappingItem',
                                    {
                                      ingredientName:
                                        mapping.ingredientName ||
                                        mapping.ingredientId,
                                    }
                                  )}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      )}
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription className="space-y-3">
                          <div>
                            {translate(
                              'pos.recipes.validation.fixInstructions',
                              'To fix these issues, you can automatically create inventory items for missing ingredients, or manually create them in the inventory management page.'
                            )}
                          </div>
                          {validation.missingMappings.length > 0 && (
                            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                              <p className="text-sm text-blue-800 dark:text-blue-200">
                                {translate(
                                  'pos.recipes.validation.autoFix.warning',
                                  'Note: Auto-fix creates basic inventory items with default settings. Package information (item count, dimensions, weight, volume) must be reviewed and configured manually by the store manager.'
                                )}
                              </p>
                            </div>
                          )}
                          <div className="flex flex-col sm:flex-row gap-2">
                            {validation.missingMappings.length > 0 && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={handleAutoFixMappings}
                                disabled={autoFixing}
                                className="flex items-center space-x-2"
                              >
                                {autoFixing ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span>
                                      {translate(
                                        'pos.recipes.validation.autoFix.processing',
                                        'Processing...'
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <Wand2 className="h-4 w-4" />
                                    <span>
                                      {translate(
                                        'pos.recipes.validation.autoFix.button',
                                        'Auto-fix Missing Mappings'
                                      )}
                                    </span>
                                  </>
                                )}
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setValidationDialogOpen(false);
                                navigate('/inventory');
                              }}
                              className="flex items-center space-x-2"
                            >
                              <ExternalLink className="h-4 w-4" />
                              <span>
                                {translate(
                                  'pos.recipes.validation.goToMappings',
                                  'Go to Inventory Management'
                                )}
                              </span>
                            </Button>
                          </div>
                        </AlertDescription>
                      </Alert>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setValidationDialogOpen(false)}>
              {translate('common.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              {translate('pos.orders.modern.pagination.showing', {
                start: Math.min(
                  (pagination.page - 1) * pagination.perPage + 1,
                  pagination.total
                ),
                end: Math.min(
                  pagination.page * pagination.perPage,
                  pagination.total
                ),
                total: pagination.total,
              })}{' '}
              {translate('pos.recipes.list', 'recipes')}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.previous',
                  'Previous'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page * pagination.perPage >= pagination.total
                }
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.next',
                  'Next'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
